import React from 'react';
import { 
  Calendar as CalendarIcon, X, Clock, MapPin, Video, 
  ExternalLink, Paperclip, Users, Bot, Edit2, Trash2,
  Check, HelpCircle, XCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { cn } from '../../lib/utils';
import { EVENT_TYPES, RSVP_STATUS } from './calendarConfig';
import { LinkedEntitiesDisplay } from './PlatformLinking';

// ============================================================================
// EVENT DETAIL PANEL
// ============================================================================
export const EventDetailPanel = ({ event, onClose, onEdit, onDelete, onRsvp, onNavigate }) => {
  if (!event) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground">
        <div className="text-center">
          <CalendarIcon className="h-12 w-12 mx-auto mb-2 opacity-30" />
          <p>Select an event to view details</p>
        </div>
      </div>
    );
  }

  const eventType = EVENT_TYPES[event.event_type] || EVENT_TYPES.other;
  const Icon = eventType.icon;
  const startDate = new Date(event.start_date);
  const endDate = new Date(event.end_date);
  const userRsvpStatus = event.user_rsvp ? RSVP_STATUS[event.user_rsvp] : null;

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 border-b flex items-center justify-between">
        <h3 className="font-semibold">Event Details</h3>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-4">
          {/* Title */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div 
                className="p-2 rounded-lg"
                style={{ backgroundColor: eventType.lightColor }}
              >
                <Icon className="h-5 w-5" style={{ color: eventType.color }} />
              </div>
              <div>
                <h2 className="text-xl font-semibold">{event.title}</h2>
                <Badge variant="outline" style={{ borderColor: eventType.color, color: eventType.color }}>
                  {eventType.label}
                </Badge>
              </div>
            </div>
          </div>

          {/* Time */}
          <div className="flex items-center gap-3 text-sm">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <div>
              <div className="font-medium">
                {startDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              </div>
              <div className="text-muted-foreground">
                {startDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} - 
                {endDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
              </div>
            </div>
          </div>

          {/* RSVP Section */}
          {event.rsvp_required && (
            <div className="p-3 bg-muted/50 rounded-lg">
              <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                <Users className="h-4 w-4" />
                RSVP Required
              </h4>
              {userRsvpStatus && (
                <div className={cn("text-sm mb-2 flex items-center gap-2 px-2 py-1 rounded", userRsvpStatus.bg)}>
                  <userRsvpStatus.icon className={cn("h-4 w-4", userRsvpStatus.color)} />
                  <span className={userRsvpStatus.color}>Your response: {userRsvpStatus.label}</span>
                </div>
              )}
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  variant={event.user_rsvp === 'accepted' ? 'default' : 'outline'}
                  className="flex-1"
                  onClick={() => onRsvp(event.id, 'accepted')}
                >
                  <Check className="h-3 w-3 mr-1" />
                  Accept
                </Button>
                <Button 
                  size="sm" 
                  variant={event.user_rsvp === 'tentative' ? 'default' : 'outline'}
                  className="flex-1"
                  onClick={() => onRsvp(event.id, 'tentative')}
                >
                  <HelpCircle className="h-3 w-3 mr-1" />
                  Maybe
                </Button>
                <Button 
                  size="sm" 
                  variant={event.user_rsvp === 'declined' ? 'destructive' : 'outline'}
                  className="flex-1"
                  onClick={() => onRsvp(event.id, 'declined')}
                >
                  <XCircle className="h-3 w-3 mr-1" />
                  Decline
                </Button>
              </div>
            </div>
          )}

          {/* Location */}
          {event.location && (
            <div className="flex items-center gap-3 text-sm">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span>{event.location}</span>
            </div>
          )}

          {/* Video Link */}
          {event.video_link && (
            <div className="flex items-center gap-3 text-sm">
              <Video className="h-4 w-4 text-muted-foreground" />
              <a href={event.video_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                Join Video Meeting <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          )}

          {/* Description */}
          {event.description && (
            <div>
              <h4 className="text-sm font-medium mb-1">Description</h4>
              <p className="text-sm text-muted-foreground">{event.description}</p>
            </div>
          )}

          {/* Attachments */}
          {event.attachments && event.attachments.length > 0 && (
            <div>
              <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                <Paperclip className="h-4 w-4" />
                Attachments ({event.attachments.length})
              </h4>
              <div className="space-y-1">
                {event.attachments.map((attachment, idx) => (
                  <a
                    key={idx}
                    href={attachment}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-blue-600 hover:underline p-2 bg-muted/50 rounded"
                  >
                    <Paperclip className="h-3 w-3" />
                    {attachment.split('/').pop() || `Attachment ${idx + 1}`}
                    <ExternalLink className="h-3 w-3 ml-auto" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Attendees */}
          {event.attendee_details?.length > 0 && (
            <div>
              <h4 className="text-sm font-medium mb-2">Attendees ({event.attendee_details.length})</h4>
              <div className="space-y-1">
                {event.attendee_details.map(att => (
                  <div key={att.id} className="flex items-center gap-2 text-sm">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium">
                      {att.first_name?.[0]}{att.last_name?.[0]}
                    </div>
                    <span>{att.first_name} {att.last_name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Platform Linked Entities */}
          <LinkedEntitiesDisplay event={event} onNavigate={onNavigate} />

          {/* AI Badge */}
          {event.created_by_ai && (
            <Badge className="bg-teal-100 text-teal-700 border-teal-200">
              <Bot className="h-3 w-3 mr-1" />
              AI Generated
            </Badge>
          )}
        </div>
      </ScrollArea>
      <div className="p-4 border-t flex gap-2">
        <Button variant="outline" className="flex-1" onClick={() => onEdit(event)}>
          <Edit2 className="h-4 w-4 mr-2" />
          Edit
        </Button>
        <Button variant="destructive" size="icon" onClick={() => onDelete(event.id)}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
